import type { ReactNode } from 'react';
import { EmptyState } from './EmptyState';

export interface Column<Row> {
  key: string;
  header: ReactNode;
  cell: (row: Row) => ReactNode;
  /** Right-align money and counts. */
  align?: 'left' | 'right' | 'center';
  width?: string;
}

export interface DataTableProps<Row> {
  rows: readonly Row[];
  columns: ReadonlyArray<Column<Row>>;
  rowKey: (row: Row) => string;
  rowClassName?: (row: Row) => string | undefined;
  onRowClick?: (row: Row) => void;
  empty?: ReactNode;
}

export function DataTable<Row>({
  rows,
  columns,
  rowKey,
  rowClassName,
  onRowClick,
  empty,
}: DataTableProps<Row>): ReactNode {
  if (rows.length === 0) {
    return <>{empty ?? <EmptyState title="Nothing to show yet." />}</>;
  }

  return (
    <div className="table-scroll">
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} style={{ textAlign: c.align ?? 'left', width: c.width }}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className={rowClassName?.(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={onRowClick ? { cursor: 'pointer' } : undefined}
            >
              {columns.map((c) => (
                <td key={c.key} style={{ textAlign: c.align ?? 'left' }}>
                  {c.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
